import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CSSProperties, ReactElement } from 'react'

export interface SortableFlexElementContentItem {
  key: string
  render: () => ReactElement
}

interface SortableItemProperties {
  item: SortableFlexElementContentItem
}

const SortableItem = ({ item }: SortableItemProperties): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.key,
  })

  const style: CSSProperties = {
    transition,
    transform: CSS.Transform.toString(transform),
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {item.render()}
    </div>
  )
}

interface Argument {
  items: SortableFlexElementContentItem[]
  dragging: boolean
  className?: string
  axis?: 'x' | 'y' | 'xy'
  onSortEnd?: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void
  onSortStart?: () => void
}

export const SortableFlexElementContent = ({
  items,
  dragging,
  className,
  axis = 'x',
  onSortEnd,
  onSortStart,
}: Argument): ReactElement => {
  const itemIds: UniqueIdentifier[] = items.map((item) => item.key)
  const strategy = axis === 'x' ? horizontalListSortingStrategy : verticalListSortingStrategy

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  )

  const style: CSSProperties = dragging ? { cursor: 'grabbing' } : { cursor: 'grab' }

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over !== null && active.id !== over.id && onSortEnd !== undefined) {
      const oldIndex = itemIds.indexOf(active.id)
      const newIndex = itemIds.indexOf(over.id)
      onSortEnd({ oldIndex, newIndex })
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => {
        onSortStart?.()
      }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        <div data-testid="sortable-flex-element-content" className={className} style={style}>
          {items.map((item) => (
            <SortableItem key={`item-${item.key}`} item={item} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
